import { ModelElementType, UserAccountResponse } from "@/components/entities";
import axios from "axios";
import { axiosInstance } from "@/contexts/axiosApiInstance";
import { Dispatch, SetStateAction } from "react";
import { createLogger } from "@/debug/DebugingLogger";
import {
  ShareModelRequest,
  UserModelRole,
  SharedAccess,
  RevokeAccessRequest,
  UserIdentification,
  ModelDeletionAnalysis,
  TrashModel,
} from "target_wasm";
import { RemoveAccessData } from "@/components/shareSection/entities";
import { wasmBody } from "@/components/common/wasmBody";
import { activeOrgForRequests } from "@/components/main/stores/orgContextStore";
const log = createLogger("ModelServices");

const fetchModels = async (
  ownedOnly?: boolean,
  /// `unscoped` opts OUT of the active-org narrowing for callers that need every
  /// org the caller can reach — the D42 transfer picker, where a personal-org
  /// table may legally move into another org's model. Deliberately explicit:
  /// silently dropping the narrowing for everyone would undo S6c.
  options?: { unscoped?: boolean },
): Promise<ModelElementType[]> => {
  try {
    const data: { owned_only?: boolean; org_id?: string } = {};
    if (ownedOnly) {
      data.owned_only = true;
    }
    // S6c/D41: the active org context NARROWS the listing to that org (plus
    // catalog and grants from orgs the caller cannot enter).
    const org = activeOrgForRequests();
    if (org.kind === "stale") return [];
    if (org.kind === "org" && !options?.unscoped) {
      data.org_id = org.orgId;
    }
    const response = await axiosInstance.post(`/action/models`, data);
    const models = response.data.map((model: Partial<ModelElementType>) => {
      const createdAt = model.created_at ?? Math.floor(Date.now() / 1000);
      return {
        name: model.name || "",
        model_id: model.model_id || "",
        created_at: createdAt,
        last_modified_at: createdAt,
        state_entity: model.state_entity || [],
        version: model.version || "",

        relation: model.relation || "",
        // No fallback: the server sends null for manager-set-owned models
        // (S6b — no individual owner exists), and defaulting to the VIEWER's
        // own email rendered every such row as owned by whoever was looking.
        owner_id: model.owner_id || "",
        active_sessions: (model.active_sessions || []).map((session) =>
          UserIdentification.from_js(session),
        ),
        last_opened: model.last_opened || 0,
        user_access: model.user_access || [],
        color: model.color,
        icon: model.icon,
        scheduled_deletion_date: model.scheduled_deletion_date,
        is_starred: model.is_starred ?? false,
        last_opened_by_me: model.last_opened_by_me ?? undefined,
        is_catalog: model.is_catalog ?? false,
        space_id: model.space_id ?? null,
        via: model.via ?? "direct",
      };
    });
    return models;
  } catch (error) {
    log.error("Error fetching models:", error);
    return [];
  }
};

/**
 * Star or unstar a model for the current user. Resolves true on success.
 * Server is idempotent — safe to call twice for the same target state.
 */
export async function setModelStarred(
  modelId: string,
  starred: boolean,
): Promise<boolean> {
  try {
    if (starred) {
      await axiosInstance.post(`/action/model/${modelId}/star`);
    } else {
      await axiosInstance.delete(`/action/model/${modelId}/star`);
    }
    return true;
  } catch (error) {
    log.error("Error toggling star for model:", error);
    return false;
  }
}

export type CreateModelPayload = {
  /// Create a role-owned catalog model. Server-gated on
  /// `can_manage_public_catalog`.
  is_catalog?: boolean;
  /// Placement (S6b/S6d): the space the model is created in; absent falls
  /// back to the caller's starter space server-side (D25 staging).
  space_id?: string;
  name?: string;
  /// One of `MODEL_COLORS`; the server refuses anything else.
  color?: string;
  icon?: number;
};

/// Throws on refusal so the caller can read the door's sentence — a 403 for a
/// full org used to come back as `null` and render as a generic failure.
async function handleCreateModel(
  request?: CreateModelPayload | null,
): Promise<ModelElementType> {
  try {
    const spaceId = request?.space_id?.trim();
    const payload = {
      ...(request?.is_catalog ? { is_catalog: true } : {}),
      // A catalog model is platform-scoped.
      ...(spaceId && !request?.is_catalog ? { space_id: spaceId } : {}),
      ...(request?.name !== undefined ? { name: request.name } : {}),
      ...(request?.color !== undefined ? { color: request.color } : {}),
      ...(request?.icon !== undefined ? { icon: request.icon } : {}),
    };
    const response = await axiosInstance.post(`/action/create`, payload);
    const createdAt = response.data.created_at ?? Math.floor(Date.now() / 1000);
    return {
      name: response.data.name || "",
      model_id: response.data.model_id || "",
      created_at: createdAt,
      last_modified_at: createdAt,
      state_entity: response.data.state_entity || [],
      version: response.data.version || "",
      relation: response.data.relation || "",
      owner_id:
        typeof response.data.owner_id === "string"
          ? response.data.owner_id
          : localStorage.getItem("email") || "",
      active_sessions: (response.data.active_sessions || []).map((session) =>
        UserIdentification.from_js(session),
      ),
      last_opened: response.data.last_opened || 0,
      user_access: response.data.user_access || [],
      color: response.data.color,
      icon: response.data.icon,
      is_catalog: response.data.is_catalog === true,
      // The door resolves an absent space to the caller's starter space, so
      // the response is the only place the placement is known.
      space_id: response.data.space_id ?? null,
    };
  } catch (error) {
    log.error("Error creating model:", error);
    throw error;
  }
}

const fetchAccountDetails = async (
  email: string | null,
  setAccountDetails: Dispatch<SetStateAction<UserAccountResponse | undefined>>,
) => {
  axiosInstance
    .post("/action/user/details", email)
    .then((response) => {
      setAccountDetails(response.data);
    })
    .catch((error) => {
      log.error("Axios error occurred: %o", error);
    });
};

export async function renameModelRequest(
  modelId: string,
  name: string,
): Promise<boolean> {
  try {
    await axiosInstance.post(`/action/model/${modelId}/rename`, name);
    return true;
  } catch (error) {
    log.error("Error renaming model:", error);
    return false;
  }
}

const handleShareModel = async (
  inviterName: string,
  modelName: string,
  emails: string[],
  role: UserModelRole,
  modelId: string | undefined,
  setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>,
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  currentEmail: string,
) => {
  const data: ShareModelRequest = ShareModelRequest.new_js(
    emails,
    UserModelRole[role],
    String(modelId),
    inviterName,
    modelName,
    currentEmail,
  );
  try {
    await axiosInstance.post("/action/share_model", wasmBody(data));
    setIsSuccess(true);
    setTimeout(() => setIsSuccess(false), 2000);
  } catch (error: unknown) {
    log.error("Error sharing model:", error);
    if (axios.isAxiosError(error)) {
      setErrorMessage(error.response?.data ?? null);
    }
    setTimeout(() => setErrorMessage(null), 2000);
    // Propagate so the share dialog reports the failure (onError) instead of
    // silently showing "successfully shared" on a non-2xx — e.g. the backend
    // FGA empty-deletes 500 on a first-time share. Without this rethrow the
    // promise resolves and ShareSection.handleUserShare calls onSuccess.
    throw error;
  }
};

const fetchModelTablesForDeletion = async (
  modelId: string,
): Promise<ModelDeletionAnalysis | null> => {
  try {
    const response = await axiosInstance.get(
      `/action/model/${modelId}/tables-for-deletion`,
    );
    const data = ModelDeletionAnalysis.from_js(response.data);
    return data;
  } catch (error) {
    log.error("Error fetching tables for deletion:", error);
    return null;
  }
};

/// S6c/D41: the Trash narrows to the org the caller stands in, like the
/// overview. The door REQUIRES the context (400 without it), and the store
/// passes the org it is asking for so a slow answer can be matched against the
/// one that is current when it lands.
const fetchModelsInTrash = async (orgId: string): Promise<TrashModel[]> => {
  const apiPath = `/action/models/trash`;
  try {
    const response = await axiosInstance.get(apiPath, {
      params: { org_id: orgId },
    });
    if (!response.data || !Array.isArray(response.data)) {
      log.warning("fetchModelsInTrash: Invalid response data format");
      return [];
    }

    const trashModels = response.data
      .map((item: unknown) => {
        try {
          return TrashModel.from_js(item);
        } catch (error: unknown) {
          log.error("Error converting TrashModel from JSON:", error, item);
          return null;
        }
      })
      .filter(
        (model: TrashModel | null): model is TrashModel => model !== null,
      );
    return trashModels;
  } catch (error: unknown) {
    log.error("Error fetching models in trash:", error);
    return [];
  }
};

const handleDeleteModel = async (
  id: string,
  model_name: string,
  tableIds: string[],
): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(`/action/model/delete`, {
      model_id: id,
      model_name: model_name,
      table_ids: tableIds,
    });
    if (response.status == 200) {
      log.info("Model deleted successfully");
      return true;
    }
    return false;
  } catch (error) {
    log.error("Error deleting model:", error);
    return false;
  }
};

export type DoorOutcome = { ok: true } | { ok: false; message: string };

/// The door's own refusal text when it sent one, else the fallback.
const doorMessage = (error: unknown, fallback: string): string =>
  axios.isAxiosError(error) && typeof error.response?.data === "string"
    ? error.response.data
    : fallback;

/// Restore a model back where it was. The door refuses (409) while the model's
/// space is in its deletion window — it comes back with the space (D53); the
/// door's own text is surfaced, never replaced.
const restoreModelInto = async (modelId: string): Promise<DoorOutcome> => {
  try {
    log.info("Restoring model:", modelId);
    await axiosInstance.post(`/action/model/${modelId}/restore`);
    return { ok: true };
  } catch (error) {
    log.error("Error restoring model:", error);
    return {
      ok: false,
      message: doorMessage(error, "Failed to restore model"),
    };
  }
};

/// D65: the spaces the caller may move this model into. The server runs the
/// move door's own checks, so every id here is one the move accepts.
const fetchMoveDestinations = async (
  modelId: string,
): Promise<string[] | null> => {
  try {
    const response = await axiosInstance.get<{ space_ids: string[] }>(
      `/action/model/${modelId}/move-destinations`,
    );
    return response.data.space_ids;
  } catch (error) {
    log.error("Error loading move destinations:", error);
    return null;
  }
};

const moveModelToSpace = async (
  modelId: string,
  spaceId: string,
): Promise<DoorOutcome> => {
  try {
    await axiosInstance.post(`/action/model/${modelId}/move`, {
      space_id: spaceId,
    });
    return { ok: true };
  } catch (error) {
    log.error("Error moving model:", error);
    return { ok: false, message: doorMessage(error, "Failed to move model") };
  }
};

const handleHardDeleteModel = async (modelId: string): Promise<boolean> => {
  try {
    log.info("Hard deleting model:", modelId);
    const response = await axiosInstance.delete(
      `/action/model/${modelId}/hard-delete`,
    );
    if (response.status === 200) {
      return true;
    }
    return false;
  } catch (error) {
    log.error("Error hard deleting model:", error);
    return false;
  }
};

export interface FetchSharedResult {
  sharedAccess: SharedAccess;
  continuationToken: string | null;
}

const fetchModelSharedWithList = async (
  modelId: string,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  search?: string,
  setContinuationToken?: Dispatch<SetStateAction<string | null>>,
  continuationToken?: string | null,
  limit?: number,
): Promise<FetchSharedResult | null> => {
  try {
    const params = new URLSearchParams();
    if (search) {
      params.append("search", search);
    }
    if (continuationToken) {
      params.append("continuation_token", continuationToken);
    }
    if (limit !== undefined) {
      params.append("limit", String(limit));
    }
    const queryString = params.toString();
    const url = `/action/model/${modelId}/shared_with/users${queryString ? `?${queryString}` : ""}`;
    const response = await axiosInstance.get(url);
    const data = response.data;

    const rawSharedAccess = {
      users: data.users ?? [],
    };
    const newAccess = SharedAccess.from_js(rawSharedAccess);
    const nextToken: string | null = data.continuation_token ?? null;

    if (continuationToken) {
      setSharedWithList((prev) => {
        if (prev) {
          return prev.extend_js(rawSharedAccess);
        }
        return newAccess;
      });
    } else {
      setSharedWithList((prev) =>
        prev ? prev.replace_users_js(rawSharedAccess) : newAccess,
      );
    }

    setContinuationToken?.(nextToken);

    log.info("Shared with list fetched successfully");
    return { sharedAccess: newAccess, continuationToken: nextToken };
  } catch (error) {
    log.error("Error fetching shared with list:", error);
    return null;
  }
};

const handleModelRevokeAccess = async (
  modelId: string,
  data: RemoveAccessData | null,
  setSharedWithList: Dispatch<SetStateAction<SharedAccess | undefined>>,
  modelName: string,
) => {
  if (!data?.clientId) return;

  const request = RevokeAccessRequest.new_js(
    JSON.stringify({ users: [data.clientId] }),
    modelId,
    data.oldRole,
    modelName,
  );
  const body = wasmBody(request);

  try {
    const response = await axiosInstance.post(
      `/action/revoke_access/model`,
      body,
    );
    log.info("Access revoked successfully:", response.data);
    setSharedWithList((prevList) =>
      prevList?.remove_user_by_email(data.email ?? ""),
    );
  } catch (error) {
    log.error("Failed to revoke access:", error);
    throw error;
  }
};

export const updateModelUserAccess = async (
  modelId: string,
  userEmail: string,
  newRole: string,
): Promise<void> => {
  await axiosInstance.post("/action/update_model_access_level", {
    model_id: modelId,
    user_email: userEmail,
    new_role: newRole,
  });
};

export async function updateModelAppearance(
  modelId: string,
  appearance: { color?: string; icon?: number },
): Promise<boolean> {
  try {
    await axiosInstance.patch(
      `/action/model/${modelId}/appearance`,
      appearance,
    );
    return true;
  } catch (error) {
    log.error("Error updating model appearance:", error);
    return false;
  }
}

export {
  fetchModels,
  handleCreateModel,
  fetchAccountDetails,
  handleShareModel,
  handleDeleteModel,
  restoreModelInto,
  fetchMoveDestinations,
  moveModelToSpace,
  handleHardDeleteModel,
  fetchModelTablesForDeletion,
  fetchModelsInTrash,
  fetchModelSharedWithList,
  handleModelRevokeAccess,
};
